import { getCategories, getChildSubcategories } from '@/api/category'
import { getCache, setCache, CacheKeys } from '@/utils/cache'
import { locale } from '@/i18n'

const CATEGORIES_TTL = 30 * 60 * 1000; // 30 minutes

export default {
	namespaced: true,
	state: {
		categories: [],
		fetchCategoriesSuccess: false,
		totalPrice: 0,
	},
	getters: {
		categories(state) {
			return state.categories
		},
		fetchCategoriesSuccess(state) {
			return state.fetchCategoriesSuccess
		},
	},
	mutations: {
		SET_CATEGORIES(state, categories) {
			state.categories = categories
		},
		SET_CATEGORIES_SUCCESS(state, success) {
			state.fetchCategoriesSuccess = success
		},
		UPDATE_TOTAL_PRICE(state, price) {
			state.totalPrice = price
		},
	},
	actions: {
		async fetchCategories({ commit }, { forceRefresh = false } = {}) {
			try {
				const cacheKey = `${CacheKeys.CATEGORIES}_${locale.value}`;
				if (!forceRefresh) {
					const cachedCategories = getCache(cacheKey);
					if (cachedCategories) {
						const firstSub = cachedCategories
							.flatMap(c => c.subcategories || [])
							.find(s => s && s.id);
						const hasCoverField = !firstSub || ('cover_image' in firstSub);
						if (hasCoverField) {
							const processedCache = cachedCategories.map(cat => ({
								...cat,
								subcategories: (cat.subcategories || []).map(sub => ({
									...sub,
									children_count: parseInt(sub.children_count) || 0
								}))
							}));
							commit('SET_CATEGORIES_SUCCESS', true);
							commit('SET_CATEGORIES', processedCache);
							return { success: true, categories: processedCache, fromCache: true };
						}
					}
				}

				const response = await getCategories()
				
				if (!response || !response.success) {
					console.error('Failed to fetch categories:', response)
					commit('SET_CATEGORIES_SUCCESS', false)
					commit('SET_CATEGORIES', [])
					return response
				}
				
				commit('SET_CATEGORIES_SUCCESS', true)
				
				const categoriesWithImages = (response.categories || []).map(cat => ({
					...cat,
					image: cat.icon,
					subcategories: (cat.subcategories || [])
						.filter(sub => sub && sub.id !== null)
						.map(sub => {
							const childCount = parseInt(sub.children_count) || 0;
							return {
								...sub,
								children_count: childCount,
								elements: (sub.elements || [])
									.filter(el => el && el.id !== null)
									.map(el => ({
										...el,
										image: el.image_url
									}))
							};
						})
				}))
				
				commit('SET_CATEGORIES', categoriesWithImages)
				
				setCache(cacheKey, categoriesWithImages, CATEGORIES_TTL);
				
				return response
			} catch (error) {
				console.error('Error fetching categories:', error)
				commit('SET_CATEGORIES_SUCCESS', false)
				commit('SET_CATEGORIES', [])
				throw error
			}
		},
		async fetchChildSubcategories(_, parentId) {
			try {
				const response = await getChildSubcategories(parentId);
				if (response && response.success) {
					return (response.subcategories || []).map(sub => ({
						...sub,
						children_count: parseInt(sub.children_count) || 0
					}));
				}
				return [];
			} catch (error) {
				console.error('Error fetching child subcategories:', error);
				return [];
			}
		},
	},
}
